<script>
import { layoutComputed, layoutMethods, authComputed } from "@/state/helpers";
import simplebar from "simplebar-vue";
import api from "@/services/http";

// Module-level cache of the flattened, access-filtered search index (built from
// /menus, which the API already scopes to the user's permissions). The layout
// remounts the navbar on every navigation, so cache it once. Cleared on logout
// via clearSearchIndexCache().
let searchIndexCache = null;

export function clearSearchIndexCache() {
  searchIndexCache = null;
}

const ROLE_LABEL_OVERRIDES = {
  "hr-admin": "HR Admin",
};

const NOTIFICATION_POLL_MS = 20000;

function relativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  // Older than a day: render as APRIL 24, 2026 — matches existing screenshot style.
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }).toUpperCase();
}

export default {
  components: { simplebar },
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      searchQuery: "",
      searchSuggestions: [],
      searchResults: [],
      showSuggest: false,
      activeIndex: -1,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1200,
    };
  },
  computed: {
    ...layoutComputed,
    ...authComputed,
    isDark() {
      return this.mode === "dark";
    },
    isMobile() {
      return this.windowWidth < 992;
    },
    displayName() {
      return this.currentUser?.name || "Guest";
    },
    displayRole() {
      const slug = this.currentUser?.roles?.[0];
      if (!slug) return "Self-Service";
      if (ROLE_LABEL_OVERRIDES[slug]) return ROLE_LABEL_OVERRIDES[slug];
      return slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    },
    employeeNo() {
      return this.currentUser?.employee_no || "";
    },
    showRoleBadge() {
      // Plain employees don't need a role badge — the employee no. is enough.
      return this.currentUser?.roles?.[0] !== "employee";
    },
    isAdmin() {
      const roles = this.currentUser?.roles || [];
      return roles.includes("admin") || roles.includes("hr-admin");
    },
    photoUrl() {
      return this.currentUser?.photo_url || "";
    },
    initials() {
      const name = this.currentUser?.name || "";
      if (!name) return "•";
      return name.split(" ").map((p) => p[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
    },
  },
  methods: {
    ...layoutMethods,
    toggleHamburgerMenu() {
      const windowSize = document.documentElement.clientWidth;
      const layoutType = document.documentElement.getAttribute("data-layout");

      document.documentElement.setAttribute("data-sidebar-visibility", "show");
      const visiblilityType = document.documentElement.getAttribute("data-sidebar-visibility");

      if (windowSize > 767) {
        const bars = document.querySelector(".ws-topbar-hamburger__bars");
        if (bars) bars.classList.toggle("open");
      }

      if (layoutType === "horizontal") {
        document.body.classList.toggle("menu");
      }

      if (visiblilityType === "show" && (layoutType === "vertical" || layoutType === "semibox")) {
        if (windowSize < 1025 && windowSize > 767) {
          document.body.classList.remove("vertical-sidebar-enable");
          document.documentElement.getAttribute("data-sidebar-size") == "sm"
            ? document.documentElement.setAttribute("data-sidebar-size", "")
            : document.documentElement.setAttribute("data-sidebar-size", "sm");
        } else if (windowSize > 1025) {
          document.body.classList.remove("vertical-sidebar-enable");
          document.documentElement.getAttribute("data-sidebar-size") == "lg"
            ? document.documentElement.setAttribute("data-sidebar-size", "sm")
            : document.documentElement.setAttribute("data-sidebar-size", "lg");
        } else if (windowSize <= 767) {
          const isOpen = document.body.classList.contains("vertical-sidebar-enable");
          if (isOpen) {
            document.body.classList.remove("vertical-sidebar-enable");
            document.documentElement.setAttribute("data-sidebar-visibility", "hidden");
          } else {
            document.body.classList.add("vertical-sidebar-enable");
            document.documentElement.setAttribute("data-sidebar-size", "lg");
          }
        }
      }

      if (layoutType == "twocolumn") {
        document.body.classList.toggle("twocolumn-panel");
      }
    },
    toggleMenu() {
      this.$parent.toggleMenu();
    },
    initFullScreen() {
      document.body.classList.toggle("fullscreen-enable");
      if (
        !document.fullscreenElement &&
        !document.mozFullScreenElement &&
        !document.webkitFullscreenElement
      ) {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
        }
      } else {
        if (document.cancelFullScreen) {
          document.cancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    },
    toggleDarkMode() {
      const next = this.isDark ? "light" : "dark";
      document.documentElement.setAttribute("data-bs-theme", next);
      this.changeMode({ mode: next });
    },
    openSettings() {
      window.dispatchEvent(new CustomEvent("worksched:open-settings"));
    },
    openChangePassword() {
      window.dispatchEvent(new CustomEvent("worksched:open-change-password"));
    },
    async loadNotifications() {
      try {
        const { data } = await api.get("/notifications");
        const rows = data?.data || [];
        this.notifications = rows.map((n) => ({
          id: n.id,
          initials: n.initials || "•",
          title: n.title || "",
          message: n.message || "",
          time: relativeTime(n.created_at),
          link_url: n.link_url || null,
          read: !!n.read_at,
        }));
        this.unreadCount = this.notifications.filter((n) => !n.read).length;
      } catch (e) {
        // Auth/network failures are surfaced via the http interceptor; swallow here
        // so an expired session doesn't crash the topbar.
        this.notifications = [];
        this.unreadCount = 0;
      }
    },
    async markRead(notification) {
      if (notification.read) return;
      // Optimistic update first, then PATCH; revert on failure.
      notification.read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      try {
        await api.post(`/notifications/${notification.id}/read`);
      } catch (e) {
        notification.read = false;
        this.unreadCount += 1;
      }
      if (notification.link_url) {
        this.$router.push(notification.link_url);
      }
    },
    async markAllRead() {
      const wasUnread = this.notifications.filter((n) => !n.read);
      if (!wasUnread.length) return;
      wasUnread.forEach((n) => (n.read = true));
      this.unreadCount = 0;
      try {
        await api.post("/notifications/read-all");
      } catch (e) {
        // Revert if the server rejected — keep the UI in sync with persisted state.
        wasUnread.forEach((n) => (n.read = false));
        this.unreadCount = wasUnread.length;
      }
    },
    // ---- Access-aware global search (suggestions from /menus) ----
    async loadSearchIndex() {
      if (searchIndexCache) {
        this.searchSuggestions = searchIndexCache;
        return;
      }
      try {
        const { data } = await api.get("/menus");
        const flat = this.flattenMenus(data?.data || []);
        searchIndexCache = flat;
        this.searchSuggestions = flat;
      } catch (e) {
        // /menus is access-scoped server-side; on failure just disable suggestions.
        this.searchSuggestions = [];
      }
    },
    flattenMenus(items, group) {
      const out = [];
      for (const it of items || []) {
        if (it.route) {
          out.push({ label: it.name, route: it.route, icon: it.icon || null, group: group || null });
        }
        if (it.children && it.children.length) {
          out.push(...this.flattenMenus(it.children, it.name));
        }
      }
      return out;
    },
    onSearchInput() {
      // Debounce so we don't re-filter on every keystroke.
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.runSearch(), 200);
    },
    runSearch() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) {
        this.searchResults = [];
        this.showSuggest = false;
        this.activeIndex = -1;
        return;
      }
      this.searchResults = this.searchSuggestions
        .filter((s) => s.label.toLowerCase().includes(q) || (s.group && s.group.toLowerCase().includes(q)))
        .slice(0, 8);
      this.showSuggest = true;
      this.activeIndex = this.searchResults.length ? 0 : -1;
    },
    onSearchFocus() {
      if (this.searchQuery.trim()) this.runSearch();
    },
    moveActive(dir) {
      if (!this.showSuggest || !this.searchResults.length) return;
      const n = this.searchResults.length;
      this.activeIndex = (this.activeIndex + dir + n) % n;
    },
    selectResult(item) {
      this.showSuggest = false;
      this.searchQuery = "";
      this.searchResults = [];
      this.activeIndex = -1;
      if (item?.route && item.route !== this.$route.path) this.$router.push(item.route);
    },
    closeSuggest() {
      this.showSuggest = false;
      this.activeIndex = -1;
    },
    highlightParts(text) {
      const q = this.searchQuery.trim();
      const src = String(text == null ? "" : text);
      if (!q) return [{ text: src, match: false }];
      const lower = src.toLowerCase();
      const ql = q.toLowerCase();
      const parts = [];
      let idx = 0;
      let pos = lower.indexOf(ql, idx);
      while (pos !== -1) {
        if (pos > idx) parts.push({ text: src.slice(idx, pos), match: false });
        parts.push({ text: src.slice(pos, pos + q.length), match: true });
        idx = pos + q.length;
        pos = lower.indexOf(ql, idx);
      }
      if (idx < src.length) parts.push({ text: src.slice(idx), match: false });
      return parts;
    },
    onSearch() {
      if (!this.searchResults.length) return;
      const pick = this.activeIndex >= 0 ? this.searchResults[this.activeIndex] : this.searchResults[0];
      this.selectResult(pick);
    },
  },
  mounted() {
    document.documentElement.setAttribute("data-bs-theme", this.mode || "light");

    document.addEventListener("scroll", function () {
      const pageTopbar = document.getElementById("page-topbar");
      if (pageTopbar) {
        document.body.scrollTop >= 50 || document.documentElement.scrollTop >= 50
          ? pageTopbar.classList.add("topbar-shadow")
          : pageTopbar.classList.remove("topbar-shadow");
      }
    });

    this.onResize = () => { this.windowWidth = window.innerWidth; };
    window.addEventListener("resize", this.onResize);

    // Close the suggestion panel when clicking anywhere outside the search box.
    this.onDocClick = (e) => {
      const box = this.$refs.searchBox;
      if (this.showSuggest && box && !box.contains(e.target)) this.closeSuggest();
    };
    document.addEventListener("mousedown", this.onDocClick);

    this.loadSearchIndex();
    this.loadNotifications();
    // Light poll so newly-filed / newly-approved notifications appear without a hard refresh.
    this.notificationTimer = setInterval(() => this.loadNotifications(), NOTIFICATION_POLL_MS);
    // Refresh the moment the user returns to this tab — common case where the
    // approver was on another window when the employee submitted.
    this.onVisibility = () => {
      if (document.visibilityState === "visible") this.loadNotifications();
    };
    this.onFocus = () => this.loadNotifications();
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("focus", this.onFocus);
  },
  beforeUnmount() {
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this.onDocClick) document.removeEventListener("mousedown", this.onDocClick);
    clearTimeout(this._searchTimer);
    if (this.notificationTimer) clearInterval(this.notificationTimer);
    if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.onFocus) window.removeEventListener("focus", this.onFocus);
  },
};
</script>

<template>
  <header id="page-topbar">
    <div class="layout-width">
      <div class="navbar-header">
        <div class="d-flex align-items-center">
          <button
            v-if="isMobile"
            type="button"
            class="ws-topbar-hamburger"
            id="topnav-hamburger-icon"
            aria-label="Toggle menu"
            :style="{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '42px',
              height: '42px',
              background: 'transparent',
              border: 0,
              padding: 0,
              marginLeft: 0,
              marginRight: '0.5rem',
              cursor: 'pointer',
              color: '#495057',
              zIndex: 1100,
            }"
            @click="toggleHamburgerMenu"
          >
            <span class="ws-topbar-hamburger__bars">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          <div class="navbar-brand-box horizontal-logo">
            <router-link to="/" class="logo logo-dark">
              <span class="logo-sm">
                <img src="@/assets/images/logo-sm.png" alt="WorkSched" height="32" />
              </span>
              <span class="logo-lg">
                <img src="@/assets/images/logo-dark.png" alt="WorkSched" height="32" />
              </span>
            </router-link>

            <router-link to="/" class="logo logo-light">
              <span class="logo-sm">
                <img src="@/assets/images/logo-sm.png" alt="WorkSched" height="32" />
              </span>
              <span class="logo-lg">
                <img src="@/assets/images/logo-light.png" alt="WorkSched" height="32" />
              </span>
            </router-link>
          </div>

          <form class="app-search d-none d-md-block ms-2" @submit.prevent="onSearch">
            <div ref="searchBox" class="position-relative ws-search">
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search..."
                autocomplete="off"
                @input="onSearchInput"
                @focus="onSearchFocus"
                @keydown.down.prevent="moveActive(1)"
                @keydown.up.prevent="moveActive(-1)"
                @keydown.esc="closeSuggest"
              />
              <span class="mdi mdi-magnify search-widget-icon"></span>

              <div v-if="showSuggest" class="ws-search-suggest">
                <template v-if="searchResults.length">
                  <button
                    v-for="(r, i) in searchResults"
                    :key="r.route"
                    type="button"
                    class="ws-search-suggest__item"
                    :class="{ 'is-active': i === activeIndex }"
                    @mouseenter="activeIndex = i"
                    @mousedown.prevent="selectResult(r)"
                  >
                    <span class="ws-search-suggest__icon">
                      <i :class="r.icon || 'bx bx-chevron-right'"></i>
                    </span>
                    <span class="ws-search-suggest__text">
                      <span class="ws-search-suggest__label">
                        <template v-for="(part, pi) in highlightParts(r.label)" :key="pi"><mark v-if="part.match">{{ part.text }}</mark><template v-else>{{ part.text }}</template></template>
                      </span>
                      <span v-if="r.group" class="ws-search-suggest__group">{{ r.group }}</span>
                    </span>
                  </button>
                </template>
                <div v-else class="ws-search-suggest__empty">
                  No matches for "{{ searchQuery }}"
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="d-flex align-items-center">
          <div class="ms-1 header-item d-none d-sm-flex">
            <BButton
              type="button"
              variant="ghost-secondary"
              class="btn-icon btn-topbar rounded-circle"
              data-toggle="fullscreen"
              @click="initFullScreen"
            >
              <i class="bx bx-fullscreen fs-22"></i>
            </BButton>
          </div>

          <div class="ms-1 header-item d-none d-sm-flex">
            <BButton
              type="button"
              variant="ghost-secondary"
              class="btn-icon btn-topbar rounded-circle light-dark-mode"
              :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
              @click="toggleDarkMode"
            >
              <i :class="isDark ? 'bx bx-sun fs-22' : 'bx bx-moon fs-22'"></i>
            </BButton>
          </div>

          <BDropdown
            variant="ghost-dark"
            dropstart
            class="ms-1 dropdown"
            :offset="{ alignmentAxis: 57, crossAxis: 0, mainAxis: -42 }"
            toggle-class="btn-icon btn-topbar rounded-circle arrow-none"
            id="page-header-notifications-dropdown"
            menu-class="dropdown-menu-lg dropdown-menu-end p-0"
            auto-close="outside"
            @shown="loadNotifications"
          >
            <template #button-content>
              <i class="bx bx-bell fs-22"></i>
              <span
                v-if="unreadCount"
                class="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger"
              >
                {{ unreadCount }}
                <span class="visually-hidden">unread notifications</span>
              </span>
            </template>
            <div class="dropdown-head bg-primary bg-pattern rounded-top dropdown-menu-lg">
              <div class="p-3">
                <BRow class="align-items-center">
                  <BCol>
                    <h6 class="m-0 fs-16 fw-semibold text-white">Notifications</h6>
                  </BCol>
                  <BCol cols="auto" class="d-flex align-items-center gap-2">
                    <BBadge variant="light-subtle" class="bg-light-subtle text-body fs-13">
                      {{ unreadCount }} New
                    </BBadge>
                    <BButton
                      v-if="unreadCount"
                      variant="link"
                      size="sm"
                      class="text-white p-0 text-decoration-underline"
                      @click="markAllRead"
                    >
                      Mark all read
                    </BButton>
                    <BButton
                      variant="link"
                      size="sm"
                      class="text-white p-0"
                      title="Notification preferences"
                      @click="openSettings"
                    >
                      <i class="bx bx-cog fs-18"></i>
                    </BButton>
                  </BCol>
                </BRow>
              </div>
            </div>
            <simplebar data-simplebar style="max-height: 320px" class="pe-2">
              <BLink
                v-for="n in notifications"
                :key="n.id"
                href="#"
                class="text-reset notification-item d-block dropdown-item position-relative"
                :class="{ 'bg-light-subtle': !n.read }"
                @click.prevent="markRead(n)"
              >
                <div class="d-flex">
                  <div class="avatar-xs me-3 flex-shrink-0">
                    <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-13">
                      {{ n.initials }}
                    </span>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mt-0 mb-1 fs-13 fw-semibold">{{ n.title }}</h6>
                    <p class="mb-0 fs-12 text-muted">{{ n.message }}</p>
                    <p class="mb-0 fs-11 fw-medium text-uppercase text-muted mt-1">
                      <span><i class="mdi mdi-clock-outline"></i> {{ n.time }}</span>
                    </p>
                  </div>
                  <span
                    v-if="!n.read"
                    class="badge bg-primary rounded-pill align-self-start"
                    style="width: 8px; height: 8px; padding: 0"
                    aria-label="unread"
                  ></span>
                </div>
              </BLink>
              <div v-if="!notifications.length" class="text-center py-4 text-muted">
                No notifications
              </div>
            </simplebar>
          </BDropdown>

          <BDropdown
            variant="link"
            class="ms-sm-3 header-item topbar-user"
            toggle-class="rounded-circle arrow-none"
            menu-class="dropdown-menu-end"
            :offset="{ alignmentAxis: -14, crossAxis: 0, mainAxis: 0 }"
          >
            <template #button-content>
              <span class="d-flex align-items-center">
                <img
                  v-if="photoUrl"
                  class="rounded-circle header-profile-user"
                  :src="photoUrl"
                  alt="Header Avatar"
                />
                <span
                  v-else
                  class="rounded-circle header-profile-user ws-avatar-initials"
                  :title="displayName"
                >
                  {{ initials }}
                </span>
                <span class="text-start ms-xl-2">
                  <span class="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                    {{ displayName }}
                  </span>
                  <span class="d-none d-xl-block ms-1 fs-12 user-name-sub-text">
                    <span v-if="employeeNo" class="ws-emp-no">{{ employeeNo }}</span>
                    <span v-if="showRoleBadge" class="badge bg-primary-subtle text-primary ws-role-badge">{{ displayRole }}</span>
                  </span>
                </span>
              </span>
            </template>
            <h6 class="dropdown-header">Welcome!</h6>
            <router-link class="dropdown-item" to="/work-sched/profile">
              <i class="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
              <span class="align-middle">Profile</span>
            </router-link>
            <BLink class="dropdown-item" href="#" @click.prevent="openChangePassword">
              <i class="mdi mdi-lock-reset text-muted fs-16 align-middle me-1"></i>
              <span class="align-middle">Change Password</span>
            </BLink>
            <BLink class="dropdown-item" href="#" @click.prevent="openSettings">
              <i class="mdi mdi-cog-outline text-muted fs-16 align-middle me-1"></i>
              <span class="align-middle">Settings</span>
            </BLink>
            <router-link v-if="isAdmin" class="dropdown-item" to="/work-sched/user-manual">
              <i class="mdi mdi-book-open-page-variant text-muted fs-16 align-middle me-1"></i>
              <span class="align-middle">User Manual</span>
            </router-link>
            <div class="dropdown-divider"></div>
            <router-link class="dropdown-item" to="/logout">
              <i class="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
              <span class="align-middle">Logout</span>
            </router-link>
          </BDropdown>
        </div>
      </div>
    </div>
  </header>
</template>

<style>
@media (min-width: 992px) {
  #page-topbar .navbar-brand-box.horizontal-logo,
  #page-topbar #topnav-hamburger-icon {
    display: none !important;
  }
  body.ws-menu-hidden #page-topbar .navbar-brand-box.horizontal-logo {
    display: block !important;
  }
}
.ws-avatar-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #F57C00;
  color: #fff;
  font-weight: 600;
  font-size: 0.78rem;
  line-height: 1;
}

/* Global search suggestions */
.ws-search-suggest {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #e9ecef);
  border-radius: 0.6rem;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  padding: 0.35rem;
  z-index: 1050;
  max-height: 360px;
  overflow-y: auto;
}
.ws-search-suggest__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.5rem 0.6rem;
  border: 0;
  background: transparent;
  border-radius: 0.45rem;
  cursor: pointer;
  text-align: left;
  color: var(--bs-body-color, #212529);
}
.ws-search-suggest__item:hover,
.ws-search-suggest__item.is-active {
  background: rgba(245, 124, 0, 0.10);
}
.ws-search-suggest__icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 124, 0, 0.12);
  color: #F57C00;
  font-size: 1rem;
}
.ws-search-suggest__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ws-search-suggest__label {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ws-search-suggest__label mark {
  background: rgba(245, 124, 0, 0.28);
  color: inherit;
  padding: 0;
  border-radius: 2px;
}
.ws-search-suggest__group {
  font-size: 0.72rem;
  color: var(--bs-secondary-color, #6c757d);
}
.ws-search-suggest__empty {
  padding: 0.75rem 0.6rem;
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.85rem;
}
.user-name-sub-text .ws-emp-no {
  font-weight: 600;
  margin-right: 0.35rem;
}
.user-name-sub-text .ws-role-badge {
  font-weight: 500;
  font-size: 0.68rem;
  padding: 0.16em 0.5em;
  vertical-align: middle;
}
.ws-topbar-hamburger {
  display: none;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  border-radius: 0.35rem;
  margin-left: 0.5rem;
  margin-right: 0.25rem;
  color: var(--bs-body-color, #495057);
}
.ws-topbar-hamburger:hover {
  background: rgba(245, 124, 0, 0.08);
  color: #F57C00;
}
.ws-topbar-hamburger__bars {
  display: inline-block;
  position: relative;
  width: 20px;
  height: 14px;
}
.ws-topbar-hamburger__bars span {
  display: block;
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  background: currentColor;
  border-radius: 1px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.ws-topbar-hamburger__bars span:nth-child(1) { top: 0; }
.ws-topbar-hamburger__bars span:nth-child(2) { top: 6px; }
.ws-topbar-hamburger__bars span:nth-child(3) { top: 12px; }

@media (max-width: 991.98px) {
  .ws-topbar-hamburger {
    display: inline-flex !important;
  }
  body #page-topbar .navbar-brand-box {
    display: block !important;
  }
  body #page-topbar .navbar-brand-box.horizontal-logo {
    display: inline-flex !important;
    align-items: center;
    visibility: visible !important;
    position: static !important;
    width: auto !important;
    padding: 0 !important;
    background: transparent !important;
    z-index: auto !important;
  }
}
</style>
